<template>
  <fts-design-modal
    class="fts-action-design-modal"
    ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <fts-emoji v-model="formData.emoji" class="design-modal-header-emoji" />
        <input 
          type="text" 
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}" 
          :style="getStyle"
          v-model="formData.name"
          spellcheck="false"
          ref="designerName">
        <!-- color -->
        <swatches 
          class="design-modal-header-swatches"
          v-model="formData.color"
          row-length="7"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          :swatch-size="25" 
          @input="selectColor"  
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{error('name')}}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Custom</span>
      <span class="design-modal-header-description">
        special actions
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- listing -->
      <fts-form-element
        :formData="formData"
        :item="{
          'fieldName': 'menus',
          'displayName': 'Listing',
          'icon': 'fts-icon-menu-caret-down',
        }"
        comfort-design
        class="fts-col">
        <fts-select
          slot="formElement"
          slot-scope="{formData, item}"
          :form-data="formData"
          :item="item"
          :options="menus"
          comfort-design
          multiple
        >
          <fts-label-group
            slot="selected-option-container"
            slot-scope="{ option }"
            :item-id="option.id"
            :list="menus"
            emoji
          />
          <fts-label-group
            slot="option"
            slot-scope="{ option }"
            :item-id="option.id"
            :list="menus"
            emoji
          />
        </fts-select>
      </fts-form-element>
      <!-- is multiple -->
      <fts-form-element
        :item="{
          fieldName: 'isMultiple',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- is default -->
      <fts-form-element
        :item="{
          fieldName: 'isDefault',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- is list only -->
      <fts-form-element
        :item="{
          fieldName: 'isListOnly',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- is menu -->
      <fts-form-element
        :item="{
          fieldName: 'isMenu',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- script -->
      <div class="fts-form-group">
        <fts-collapsible-element
          :condition="!!formData.script"
          label="script"
          immersive
          beta
        >
          <fts-script-editor
            slot="content"
            editor-id="script"
            v-model="formData.script"
            label="Script"
            lang="js"
          />
        </fts-collapsible-element>
      </div>
      <!-- condition -->
      <div class="fts-form-group">
        <fts-collapsible-element
          :condition="!!formData.condition"
          label="condition script"
          immersive
          beta
        >
          <fts-script-editor
            slot="content"
            editor-id="conditionScript"
            v-model="formData.condition"
            label="Condition"
            lang="js"
            :disabled="conditionDisabled"
          />
        </fts-collapsible-element>
      </div>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import Swatches from "vue-swatches";
import changesets from "diff-json";

import commons from "../commons/commons";
import validator from "../commons/designValidator";
import { authorizationStore } from '../store/authorizationStore';

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSContenteditable from "../elements/FTSContenteditable";

export default {
  components: {
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-script-editor": FTSScriptEditor,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-contenteditable": FTSContenteditable,
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
  }),
  validations() {
    let formData = {};
    formData = validator.activityDesign(this.actions);
    return { formData };
  },
  methods: {
    async show(item, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(item);

      if (!this.formData.script)
        this.$set(this.formData, "script", "");
      if (!this.formData.condition)
        this.$set(this.formData, "condition", "");

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData)
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });             
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      })
    },
    done(save) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (save) {
          this.saveCallBack && this.saveCallBack(this.formData);
          this.originalData = _.cloneDeep(this.formData);
        } else {
          this.resolve && this.resolve(this.formData);
          this.reset();
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });  
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
    },
    error(key) {
      return validator.actionDesignError(this.$v.formData, key);
    },
    selectColor(val) {
      if (!val) this.$delete(this.formData, 'color');
    },
  },
  computed: {
    actions() {
      return this.$store.getters.canvasActions;
    },
    menus() {
      return this.$store.getters.canvasMenus.map(x => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji,
      }));
    },
    colors() {
      return commons.darkPalette.concat([""]);
    },
    isExist() {
      return Boolean(this.actions.find(x => x.id === this.formData.id));
    },
    getStyle() {
      if (this.formData && this.formData.name && this.formData.color)
        return {
          "color": this.formData.color,
        }
    },
    conditionDisabled() {
      return !(authorizationStore.getters.isAdministrator || Boolean(this.formData.condition));
    },
  }
}
</script>